import { ReactElement, useEffect, useRef, useState } from 'react';

import { useOnClickOutside } from 'usehooks-ts';

import { Label, LABEL_TYPE } from '@/components/generic';
import { LeftChevronIcon } from '@/resources';

type TimeInputProps = {
  label: string;
  setFieldValue: (field: string, value: number) => void;
  fieldName: string;
  defaultValue: number | undefined;
  className?: string;
  isInvalid?: boolean;
  isRequired?: boolean;
  icon?: ReactElement;
  hasSpinners?: boolean;
};

const TimeInput = ({
  label,
  setFieldValue,
  fieldName,
  defaultValue,
  className,
  isInvalid,
  isRequired = false,
  icon = undefined,
  hasSpinners = false
}: TimeInputProps) => {
  const defaultFocusedField = '- -';
  const calculateInitialTime = (totalSeconds: number | undefined) => {
    if (totalSeconds === undefined) {
      return {
        minutes: defaultFocusedField,
        seconds: defaultFocusedField
      };
    }

    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return {
      minutes: mins.toString().padStart(2, '0'),
      seconds: secs.toString().padStart(2, '0')
    };
  };

  const { minutes: initialMinutes, seconds: initialSeconds } = calculateInitialTime(defaultValue);

  const [minutes, setMinutes] = useState(initialMinutes);
  const [seconds, setSeconds] = useState(initialSeconds);
  enum TIME_INPUT_FIELD {
    MINUTES = 'minutes',
    SECONDS = 'seconds'
  }
  const [focusedField, setFocusedField] = useState<TIME_INPUT_FIELD | null>(null);
  const [isTouched, setIsTouched] = useState(false);
  const minutesRef = useRef(null);
  const secondsRef = useRef(null);

  useEffect(() => {
    const totalSeconds = Number(minutes) * 60 + Number(seconds);
    setFieldValue(fieldName, totalSeconds);
  }, [minutes, seconds, setFieldValue, fieldName]);

  const handleMinutesChange = (value: string) => {
    if (/^\d{0,2}$/.test(value) && Number(value) <= 59) {
      setMinutes(value);
    }
  };

  useOnClickOutside(minutesRef, () => {
    if (minutes === '') {
      if (hasSpinners) {
        setMinutes('0');
      } else {
        setMinutes(defaultFocusedField);
      }
    } else if (Number(minutes) < 10) {
      setMinutes(minutes.padStart(2, '0'));
    }
  });

  const handleSecondsChange = (value: string) => {
    if (/^\d{0,2}$/.test(value) && Number(value) <= 59) {
      setSeconds(value);
    }
  };

  useOnClickOutside(secondsRef, () => {
    if (seconds === '') {
      if (hasSpinners) {
        setSeconds('0');
      } else {
        setSeconds(defaultFocusedField);
      }
    } else if (Number(seconds) < 10) {
      setSeconds(seconds.padStart(2, '0'));
    }
  });

  const handleMinutesFocus = () => {
    setFocusedField(TIME_INPUT_FIELD.MINUTES);
    if (minutes === defaultFocusedField) {
      setMinutes('');
    }
  };

  const handleSecondsFocus = () => {
    setFocusedField(TIME_INPUT_FIELD.SECONDS);
    if (seconds === defaultFocusedField) {
      setSeconds('');
    }
  };

  const handleBlur = () => {
    setFocusedField(null);
  };
  const handleIncrement = () => {
    if (focusedField === TIME_INPUT_FIELD.MINUTES) {
      const min = Number(minutes);
      if (!isNaN(min) && min < 59) {
        setMinutes((min + 1).toString().padStart(2, '0'));
      }
    } else {
      const sec = Number(seconds);
      if (!isNaN(sec) && sec < 59) {
        setSeconds((sec + 1).toString().padStart(2, '0'));
      }
    }
  };

  const handleDecrement = () => {
    if (focusedField === TIME_INPUT_FIELD.MINUTES) {
      const min = Number(minutes);
      if (!isNaN(min) && min > 0) {
        setMinutes((min - 1).toString().padStart(2, '0'));
      }
    } else {
      const sec = Number(seconds);
      if (!isNaN(sec) && sec > 0) {
        setSeconds((sec - 1).toString().padStart(2, '0'));
      }
    }
  };

  const handleMinutesBlur = () => {
    setIsTouched(true);
    if (minutes === '') {
      if (hasSpinners) {
        setMinutes('00');
      } else {
        setMinutes(defaultFocusedField);
      }
    } else if (Number(minutes) < 10) {
      setMinutes(minutes.padStart(2, '0'));
    }
  };

  const handleSecondsBlur = () => {
    setIsTouched(true);
    if (seconds === '') {
      if (hasSpinners) {
        setSeconds('00');
      } else {
        setSeconds(defaultFocusedField);
      }
    } else if (Number(seconds) < 10) {
      setSeconds(seconds.padStart(2, '0'));
    }
  };

  const showError = isInvalid && isTouched;

  return (
    <div className={`flex flex-col ${className}`}>
      <Label text={`${label}${isRequired ? ' *' : ''}`} type={LABEL_TYPE.PARAGRAPH2} />
      <div
        className={`flex h-10 w-28 items-center rounded-lg border px-3 ${
          showError ? 'border-accent-red focus:outline-accent-red' : 'border-secondary-blue-gray'
        }`}>
        {icon && <div className='mr-2'>{icon}</div>}
        <input
          type='text'
          value={minutes}
          onChange={(e) => handleMinutesChange(e.target.value)}
          className='w-5 text-center outline-none'
          ref={minutesRef}
          onFocus={handleMinutesFocus}
          onBlur={() => {
            handleMinutesBlur();
            handleBlur();
          }}
        />
        <span>:</span>
        <input
          type='text'
          value={seconds}
          onChange={(e) => handleSecondsChange(e.target.value)}
          className='w-5 text-center outline-none'
          ref={secondsRef}
          onFocus={handleSecondsFocus}
          onBlur={() => {
            handleSecondsBlur();
            handleBlur();
          }}
        />

        {hasSpinners && (
          <div className='ml-2 flex cursor-pointer flex-col items-center justify-center'>
            <LeftChevronIcon
              className={
                'mr-0.5 size-3 rotate-90 text-secondary-blue-gray hover:text-secondary-dark-steel-blue'
              }
              onClick={handleIncrement}
              onMouseDown={(e) => e.preventDefault()}
            />
            <LeftChevronIcon
              className={
                'size-3 -rotate-90 text-secondary-blue-gray hover:text-secondary-dark-steel-blue'
              }
              onClick={handleDecrement}
              onMouseDown={(e) => e.preventDefault()}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default TimeInput;
